import { CheckIcon, PinFilledIcon, PinIcon, RightArrowIcon } from '@sketchnote/icons';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import Link, { LinkProps } from 'next/link';
import React, { useEffect, useState } from 'react';

import { Button } from '../button/button.component';
import { Image } from '../image';
import { Tag, TagDataType } from '../tags/tags.component';
import { Description, ImageTag, Stage, StageProps, Title, Wrapper } from './components';
export interface ModuleProps extends LinkProps {
  title: string;
  tag: TagDataType;
  coverImg?: string;
  coverBgColor?: string;
  description?: string;
  onClick?: ((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void) | undefined;
  size?: 'xl' | 'lg' | 'md';
  hasStarted?: boolean;
  className?: string;
  stage: StageProps;
  flashCardCount?: number;
  templateTaskCount?: number;
  translations: {
    bookmarked_flashcards: string;
    module_started: string;
    read_more: string;
    flashcard_count: string;
    template_count: string;
  };
  fullWidth?: boolean;
  pinned?: boolean;
  handlePinLesson?: (slug: string, pin: boolean, name: string) => void;
  slug?: string;
}

export const Module = ({
  title,
  coverImg = 'https://via.placeholder.com/150',
  tag,
  description,
  size = 'md',
  hasStarted = false,
  stage,
  coverBgColor,
  className,
  flashCardCount,
  templateTaskCount,
  translations,
  href,
  pinned,
  handlePinLesson,
  slug,
  fullWidth = false,
}: ModuleProps): JSX.Element => {
  const [pin, setPin] = useState(false);
  useEffect(() => {
    pinned && setPin(pinned);
  }, [pinned]);

  if (size === 'md') {
    return (
      <Link href={href}>
        <a>
          <Wrapper
            className={classNames({
              [`rounded overflow-hidden`]: true,
              [className || '']: !!className,
            })}
            style={{ width: '304px' }}
          >
            <div className="relative">
              <ImageTag tag={tag} src={coverImg} className="mb-16" />

              <Tippy
                className="w-auto h-auto bg-gray-999"
                placement="bottom"
                content={pin ? 'Unpin lesson from sidebar' : 'Pin lesson to sidebar'}
              >
                <div className="absolute right-8 top-8 bg-white shadow-sm rounded-sm">
                  <button
                    className="p-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handlePinLesson?.(slug!, !pin, title);
                      setPin(!pin);
                    }}
                  >
                    {pin ? (
                      <PinFilledIcon className="text-gray-500" />
                    ) : (
                      <PinIcon className="text-gray-500" />
                    )}
                  </button>
                </div>
              </Tippy>
            </div>
            <div className="mx-16">
              <Title className="mb-14">{title}</Title>
              {description ? <Description className="mb-18">{description}</Description> : null}
              {/* <Stage label={stage.label} type={stage.type} className="mb-10" /> */}
            </div>
            {flashCardCount ? (
              <Button
                status="tertiary-gray"
                className="rounded mx-16 mb-16"
                style={{ fontSize: '12px', paddingLeft: '0.8rem', paddingRight: '0.8rem' }}
                iconLeft={<CheckIcon className="ml-auto" />}
              >
                {`${tag.value === 'legal' ? '1' : templateTaskCount} ${
                  tag.value === 'legal' ? 'template task and' : translations.template_count
                } ${flashCardCount} ${translations.flashcard_count}`}
              </Button>
            ) : null}
            {hasStarted ? (
              <Button
                status="tertiary-green"
                className="rounded mx-16 mb-16"
                iconLeft={<CheckIcon className="ml-auto" />}
                style={{ fontSize: '12px', paddingLeft: '0.8rem', paddingRight: '0.8rem' }}
              >
                {translations.module_started}
              </Button>
            ) : null}
          </Wrapper>
        </a>
      </Link>
    );
  } else if (size === 'lg') {
    return (
      <Link href={href}>
        <a>
          <Wrapper
            className={classNames({
              [`items-center overflow-hidden rounded `]: true,
              [className || '']: !!className,
              'w-464': !fullWidth,
            })}
            style={{ flexDirection: 'row' }}
          >
            <div
              className="flex flex-col h-full p-10 justify-center min-h-223 overflow-hidden"
              style={{ maxWidth: '160px' }}
            >
              <Image src={coverImg} alt="card image" className="transition-all duration-500" />
            </div>

            <div className={`px-12 py-16 border-gray-200 border-l`}>
              <div className="flex justify-between mb-14">
                <div>
                  <Tag color={tag?.color}>{tag.label}</Tag>
                </div>
                <div className="flex">
                  <Stage label={stage.label} type={stage.type} />
                  <Tippy
                    className="w-auto h-auto bg-gray-999 tippy"
                    placement="bottom"
                    content={pin ? 'Unpin lesson from sidebar' : 'Pin lesson to sidebar'}
                  >
                    <div className=" bg-white shadow-sm ml-12 rounded-sm">
                      <button
                        className="p-10"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setPin(!pin);

                          handlePinLesson?.(slug!, !pin, title);
                        }}
                      >
                        {pin ? (
                          <PinFilledIcon className="text-gray-500" />
                        ) : (
                          <PinIcon className="text-gray-500" />
                        )}
                      </button>
                    </div>
                  </Tippy>
                </div>
              </div>
              <Title className="mb-14">{title}</Title>
              {description ? <Description className="mb-8">{description}</Description> : null}
              {flashCardCount ? (
                <Button
                  status="tertiary-gray"
                  className="rounded w-full"
                  iconLeft={<CheckIcon className="ml-auto" />}
                  style={{ fontSize: '12px', paddingLeft: '0.8rem', paddingRight: '0.8rem' }}
                >
                  {`${tag.value === 'legal' ? '1' : templateTaskCount} ${
                    tag.value === 'legal' ? 'template task and' : translations.template_count
                  } ${flashCardCount} ${translations.flashcard_count}`}
                </Button>
              ) : null}
              {hasStarted ? (
                <Button
                  status="tertiary-green"
                  className="rounded mx-16 mb-16"
                  iconLeft={<CheckIcon className="ml-auto" />}
                  style={{ fontSize: '12px', paddingLeft: '0.8rem', paddingRight: '0.8rem' }}
                >
                  {translations.module_started}
                </Button>
              ) : null}
            </div>
          </Wrapper>
        </a>
      </Link>
    );
  } else if (size === 'xl') {
    return (
      <Link href={href}>
        <a className="w-full">
          <Wrapper
            className={classNames({
              [`rounded max-h-336 overflow-hidden`]: true,
              [className || '']: !!className,
            })}
            style={{ maxWidth: '944px', flexDirection: 'row' }}
          >
            <div
              className={`border-r border-gray-200 rounded-tl rounded-bl w-6/12`}
              style={{ marginTop: '-0.5px', backgroundColor: coverBgColor }}
            >
              <Image
                src={coverImg}
                alt="card image"
                className="max-h-full max-w-full m-auto transition-all duration-500"
              />
            </div>
            <div className={`px-40 py-32 w-6/12`}>
              <div className="flex justify-between mb-14">
                <div>
                  <Tag color={tag?.color}>{tag.label}</Tag>
                </div>
                <div className="flex">
                  <Stage label={stage.label} type={stage.type} />
                  <Tippy
                    className="w-auto h-auto bg-gray-999 tippy"
                    placement="bottom"
                    content={pin ? 'Unpin lesson from sidebar' : 'Pin lesson to sidebar'}
                  >
                    <div className=" bg-white shadow-sm ml-12 rounded-sm">
                      <button
                        className="p-10"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setPin(!pin);
                          handlePinLesson?.(slug!, !pin, title);
                        }}
                      >
                        {pin ? (
                          <PinFilledIcon className="text-gray-500" />
                        ) : (
                          <PinIcon className="text-gray-500" />
                        )}
                      </button>
                    </div>
                  </Tippy>
                </div>
              </div>
              <Title className="mb-8">{title}</Title>
              {description ? <Description className="mb-20">{description}</Description> : null}
              <div>
                {flashCardCount ? (
                  <Button
                    status="tertiary-gray"
                    className="rounded"
                    iconLeft={<CheckIcon className="ml-auto" />}
                    style={{ fontSize: '12px', minWidth: '300px' }}
                  >
                    {`${tag.value === 'legal' ? '1' : templateTaskCount} ${
                      tag.value === 'legal' ? 'template task and' : translations.template_count
                    } ${flashCardCount} ${translations.flashcard_count}`}
                  </Button>
                ) : null}
              </div>
              {hasStarted ? (
                <Button
                  status="tertiary-green"
                  className="rounded"
                  iconLeft={<CheckIcon className="ml-auto" />}
                  style={{ fontSize: '12px', paddingLeft: '0.8rem', paddingRight: '0.8rem' }}
                >
                  {translations.module_started}
                </Button>
              ) : null}
              <Button status="ghost" className="mt-26 pl-0" iconRight={<RightArrowIcon />}>
                {translations.read_more}
              </Button>
            </div>
          </Wrapper>
        </a>
      </Link>
    );
  }
  return <></>;
};
